import { useContext, useMemo } from "react";
import { Serie } from "@nivo/line";
import { useTranslation } from "react-i18next";
import { HMICarousel } from "../../../../components";
import { OEESidebarView } from "pages/HMI/HMIDashboard/components/OEEGaugesSidebar/OEEGaugesSidebar.view";
import { TrendSlide } from "./slide";
import { HMICachedDataContext } from "contexts/HMICachedDataContext/HMICachedDataContext";
import { HMIContext } from "contexts/HMIContext/HMIContext";
import { ShiftOptionsEnum } from "components/ShiftDropdown/ShiftDropdown.view";
import { applyPendingOverlay } from "utilities/qualityIntervals";
import { buildOeeTrendData, buildPerformanceTrendData, buildQualityTrendData, ITrendWindow } from "utilities/oeeTrend";

const VIEW_LABEL_KEYS: Record<string, string> = {
    oee: "HMI.oee_sidebar.oee",
    performance: "HMI.oee_sidebar.performance",
    quality: "HMI.oee_sidebar.quality",
};

interface OEECarouselProps {
    selectedView: OEESidebarView;
}

export const OEETrendCarousel = ({ selectedView }: OEECarouselProps) => {
    const { t } = useTranslation();
    const { store } = useContext(HMICachedDataContext);
    const { shift } = useContext(HMIContext);

    const label = t(VIEW_LABEL_KEYS[selectedView] || "HMI.oee_sidebar.oee");
    const title = t("HMI.oee_trend.title", { label });

    const qualityEnabled = !!store.machineCenter?.machine_center_oee_config?.record_quality_enabled;

    const trendData = useMemo((): Serie[] => {
        if (
            selectedView !== OEESidebarView.OEE &&
            selectedView !== OEESidebarView.PERFORMANCE &&
            selectedView !== OEESidebarView.QUALITY
        ) {
            return [];
        }

        const reports = store.lastTwoShiftsEventReports;
        const isCurrent = shift === ShiftOptionsEnum.current;

        const shiftStartVal = isCurrent ? reports?.currentShiftStart : reports?.previousShiftStart;
        const shiftEndVal = isCurrent ? reports?.currentShiftEnd : reports?.previousShiftEnd;
        const trendWindow: ITrendWindow = { shiftStart: shiftStartVal, shiftEnd: shiftEndVal, timezone: reports?.timezone };

        const performanceSummary = isCurrent
            ? reports?.currentShiftPerformanceSummary
            : reports?.previousShiftPerformanceSummary;

        if (selectedView === OEESidebarView.PERFORMANCE) {
            return buildPerformanceTrendData(performanceSummary, trendWindow);
        }

        // Silent pending overlay: trend chart shows operator-submitted values
        // immediately, before the next sync. No visual indicator on the chart;
        // the indicator lives in the modal.
        const baseQualitySummary = isCurrent
            ? reports?.currentShiftQualitySummary
            : reports?.previousShiftQualitySummary;
        const qualitySummary = applyPendingOverlay(
            baseQualitySummary,
            store.manualQualityPending,
            store.machineCenter?.machine_center_id,
            shiftStartVal,
            shiftEndVal,
        );

        if (selectedView === OEESidebarView.QUALITY) {
            return buildQualityTrendData(qualitySummary, trendWindow);
        }

        // Quality recording disabled → withhold the rows so quality drops out
        // of the product (the builder treats "no quality row" as factor 1).
        return buildOeeTrendData({
            perfRecords: performanceSummary,
            qualityRecords: qualityEnabled ? qualitySummary : undefined,
            ...trendWindow,
        });
    }, [
        selectedView,
        shift,
        store.lastTwoShiftsEventReports,
        store.manualQualityPending,
        store.machineCenter?.machine_center_id,
        qualityEnabled,
    ]);

    return <HMICarousel slides={[<TrendSlide title={title} data={trendData} />]} />;
};
